import { useContext, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import useApi from 'hooks/useApi'
import Banner from 'components/Banner'
import appContext from 'context/appContext'
import NotFound404Page from 'pages/NotFound404Page/NotFound404Page'
import type { BrandsCreatePayload } from '@contracts'
import navigationHeaderStyles from '../../NavigationHeader.module.scss'
import { FormSkeleton } from 'components/Skeletons'
import PageHeader from 'components/PageHeader/PageHeader'
import { CreateAndEditNewBrandForm } from 'components/Forms'
import { PDP_GENERATOR_API_HEADERS } from 'utils/constants'
import useToast from 'hooks/useToast'
import usePermissions from 'hooks/usePermissions'
import { WppBreadcrumb, WppActionButton, WppButton } from '@wppopen/components-library-react'
import type { WppBreadcrumbCustomEvent } from '@wppopen/components-library/dist/types/components'
import type { BreadcrumbItemEventDetails } from '@wppopen/components-library'
import clsx from 'clsx'
import styles from './Brands.module.scss'
import SettingsPageContext from 'context/settingsPageContext/settingsPageContext'

export default function CreateBrand() {
  const navigate = useNavigate()
  const { setToastDetails, toastDetails } = useToast()
  const { setActiveBannerId, setToastDetailsContext } = useContext(SettingsPageContext)
  const { clientsData } = useContext(appContext)
  const { client_id } = useParams()
  const client = clientsData?.clients?.find(client => client.id === client_id)
  const {
    fetchData: postBrand,
    isLoading: isPostBrandLoading,
    url: postBrandUrl,
  } = useApi<BrandsCreatePayload>('brands')

  const { clientLevelPermissions } = usePermissions({
    clientId: client_id,
    permissionSubset: ['PDP_APP_POST_BRANDS'],
  })

  const breadCrumbItems = useMemo(
    () => [
      {
        label: 'Settings',
        path: '/settings',
      },
      {
        label: client?.client_name || 'client',
        path: `/settings/client/${client_id}`,
      },
      {
        label: 'Add brand',
        path: `/create/${client_id}/brand`,
      },
    ],
    [client_id, client?.client_name],
  )

  const [canPostBrand] = clientLevelPermissions
  const isInitialising = !clientsData

  // client does not exist in client data
  if (!isInitialising && (!client || !canPostBrand)) {
    return <NotFound404Page />
  }

  const handleSubmitForm = (brandData: BrandsCreatePayload) => {
    // this function will update the client data with a PUT and a GET
    const updateClientData = async () => {
      try {
        // POST new client data
        const resJson = await postBrand(postBrandUrl, {
          method: 'POST',
          body: JSON.stringify(brandData),
          headers: { [PDP_GENERATOR_API_HEADERS.CLIENT_IDS_HEADER]: brandData.client_id as string },
        })
        setActiveBannerId(brandData.id as string)
        setToastDetailsContext({
          message: 'Brand successfully created',
          type: 'success',
        })
        setToastDetails(undefined)
        return resJson
      } catch (error) {
        throw error
      }
    }

    // call the actual update client data function
    return new Promise((resolve, reject) => {
      updateClientData()
        .then(result => {
          resolve(result)
        })
        .catch(error => {
          reject(error)
        })
    })
  }

  const handleRouteChange = (e: WppBreadcrumbCustomEvent<BreadcrumbItemEventDetails>) => {
    if (isInitialising) return
    const { path } = e.detail
    navigate(path)
  }

  const handleCancel = () => navigate(`/settings/client/${client_id}`)

  return (
    <div className="dark-bg-page">
      <div className={clsx('light-header', styles.header)}>
        <div className={navigationHeaderStyles.navigationHeader__container}>
          <WppBreadcrumb items={breadCrumbItems} middleTruncation onWppChange={handleRouteChange} />
          <Banner
            text="The more information you provide, the better the quality of output content"
            type="info"
            shouldBannerShow={!toastDetails}
          />
          <Banner
            text={toastDetails?.message}
            type={toastDetails?.type}
            shouldBannerShow={!!toastDetails}
            onClose={() => setToastDetails(undefined)}
          />
        </div>

        <PageHeader isLoading={isInitialising} tags={[]} pageTitle="Add brand" menuItems={[]} createdDetails="" />
      </div>

      <div className={styles.wrapper}>
        <div className={clsx(styles.actions, { [styles.space_top]: isInitialising })}>
          {!isInitialising && (
            <>
              <WppActionButton variant="primary" data-testid="cancel-cta" onClick={handleCancel}>
                Cancel
              </WppActionButton>
              <WppButton
                type="submit"
                form="brand-form"
                variant="primary"
                data-testid="save-changes-cta"
                disabled={isPostBrandLoading}
              >
                Save
              </WppButton>
            </>
          )}
        </div>
        <div className={styles.formContainer}>
          {isInitialising ? (
            <FormSkeleton />
          ) : (
            <CreateAndEditNewBrandForm
              client_id={client?.id || ''}
              onSubmitForm={handleSubmitForm}
              redirectToNewBrandView
              isLoading={isPostBrandLoading}
              setToastDetails={setToastDetails}
              isFormActive
            />
          )}
        </div>
      </div>
    </div>
  )
}
